import { Button } from './button';
import { Icon } from './icons';

export interface ActiveFilterChip {
  /** Stable React key. */
  key: string;
  /** What the filter is, in words — "Needs a look", "Folder: Tax Returns". */
  label: string;
  onRemove: () => void;
}

export interface ActiveFiltersBarProps {
  chips: ActiveFilterChip[];
  /** Documents the narrowed list shows, and how many the room holds (null while
   * either is still loading — the count is then left out). */
  shown: number | null;
  total: number | null;
  onShowAll: () => void;
}

function documents(n: number): string {
  return `${n} document${n === 1 ? '' : 's'}`;
}

/**
 * Directly above the document list whenever anything narrows it (owner ask
 * 2026-10-15): what is applied — each filter a chip with its own ✕ — how many
 * documents that leaves, and one prominent "Show all documents" that clears
 * every filter and returns to the main view. Nothing when no filter is applied.
 */
export function ActiveFiltersBar({ chips, shown, total, onShowAll }: ActiveFiltersBarProps) {
  if (chips.length === 0) return null;
  return (
    <div
      role="region"
      aria-label="Active filters"
      className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-primary/30 bg-primary-soft px-3 py-2 text-sm"
    >
      <span className="font-semibold text-primary-ink">Showing:</span>
      <ul className="flex min-w-0 flex-wrap items-center gap-2" aria-label="Filters applied">
        {chips.map((chip) => (
          <li
            key={chip.key}
            className="inline-flex h-8 max-w-full items-center gap-0.5 rounded-pill border border-input bg-surface pl-3 pr-0.5 text-[13px] font-medium text-ink"
          >
            <span className="min-w-0 truncate">{chip.label}</span>
            <button
              type="button"
              onClick={chip.onRemove}
              aria-label={`Remove filter: ${chip.label}`}
              title="Remove this filter"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Icon name="close" size={14} />
            </button>
          </li>
        ))}
      </ul>
      {shown !== null && total !== null ? (
        <span className="text-ink-2" role="status">
          — {shown} of {documents(total)}
        </span>
      ) : null}
      <Button size="sm" onClick={onShowAll} className="sm:ml-auto">
        <Icon name="close" size={14} />
        Show all documents
      </Button>
    </div>
  );
}
